// Redux Saga
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { devicesApi } from 'services/apiDigitalTicket/devices';
import { deviceApi } from 'services/apiDigitalTicket/device';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Types
import { ApplicationState } from 'store';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

import * as DevicesActions from './actions';
import { DevicesTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof DevicesActions.listDevicesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(devicesApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DevicesActions.listDevicesSuccess({
          devicesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('devices - list - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DevicesActions.listDevicesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof DevicesActions.listDeviceRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(devicesApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DevicesActions.listDeviceSuccess({
          deviceData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('device - get - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DevicesActions.listDeviceFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deviceStatus({
  payload,
}: ReturnType<typeof DevicesActions.updateStatusRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando status...' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(devicesApi.updateStatus, {
      params: { transportOperatorId: transportOperator.id, ...payload },
      id: payload.id,
    });

    const userId = yield select(
      (state: ApplicationState) => state.users.appUserData.id,
    );

    if (response.status === 200) {
      yield put(DevicesActions.updateStatusSuccess());

      yield put(DialogsActions.setClose());

      yield put(
        DevicesActions.listDevicesRequest({
          userId,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('devices - update status - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DevicesActions.updateStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listDeviceStatus({
  payload,
  meta,
}: ReturnType<typeof DevicesActions.listDeviceStatusRequest>): any {
  try {
    const { body } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando dados...',
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const params = { transportOperatorId: transportOperator.id, ...body };

    const response = yield call(deviceApi.deviceStatusList, {
      params,
    });

    if (response.status === 200) {
      const totalCount = Number.parseInt(
        response.headers.totalcount || `${response?.data?.length || 0}`,
        10,
      );
      const offlineCount = Number.parseInt(
        response.headers.offlinecount || '0',
        10,
      );
      const onlineCount = Number.parseInt(
        response.headers.onlinecount || '0',
        10,
      );

      yield put(
        DevicesActions.listDeviceStatusSuccess({
          deviceStatus: {
            devices: response.data,
            total: totalCount,
            offline: offlineCount,
            online: onlineCount,
          },
        }),
      );

      yield put(DialogsActions.setClose());

      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('device - listDeviceStatus - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DevicesActions.listDeviceStatusFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

function* listDeviceTimeline({
  payload,
  meta,
}: ReturnType<typeof DevicesActions.listDeviceTimelineRequest>): any {
  try {
    const { deviceId } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: 'Carregando dados...',
      }),
    );

    const response = yield call(deviceApi.deviceTimeline, {
      params: {
        deviceId,
      },
    });

    if (response.status === 200) {
      yield put(
        DevicesActions.listDeviceTimelineSuccess({
          deviceTimeline: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
      yield meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('device - listDeviceTimeline - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DevicesActions.listDeviceTimelineFailure());
    yield put(DialogsActions.setClose());
    yield meta.promise.reject(error);
  }
}

// eslint-disable-next-line import/prefer-default-export
export const devicesSagas = [
  takeLatest(DevicesTypes.LIST_DEVICE_REQUEST, get),
  takeLatest(DevicesTypes.LIST_DEVICES_REQUEST, list),
  takeLatest(DevicesTypes.UPDATE_STATUS_REQUEST, deviceStatus),
  takeLatest(DevicesTypes.LIST_DEVICE_STATUS_REQUEST, listDeviceStatus),
  takeLatest(DevicesTypes.LIST_DEVICE_TIMELINE_REQUEST, listDeviceTimeline),
];
